import { useCallback, useRef, useState } from "react";
import type { FilterState } from "../contexts/ViewStateContext";

interface FilterHistoryEntry {
	filters: FilterState;
	timestamp: number;
}

/**
 * Hook for managing filter history with undo/redo functionality
 * Maintains a stack of filter states with configurable max history size
 */
export function useFilterHistory(
	initialFilters: FilterState,
	maxHistorySize = 50,
) {
	const [history, setHistory] = useState<FilterHistoryEntry[]>([
		{ filters: initialFilters, timestamp: Date.now() },
	]);
	const [currentIndex, setCurrentIndex] = useState(0);

	// Track if we're in the middle of an undo/redo operation
	const isUndoRedoRef = useRef(false);

	// Check if undo is available
	const canUndo = currentIndex > 0;

	// Check if redo is available
	const canRedo = currentIndex < history.length - 1;

	// Add new filter state to history
	const pushState = useCallback(
		(filters: FilterState) => {
			// Don't add to history if we're in undo/redo mode
			if (isUndoRedoRef.current) {
				isUndoRedoRef.current = false;
				return;
			}

			setHistory((prev) => {
				// Remove any future states if we're not at the end
				const newHistory = prev.slice(0, currentIndex + 1);

				// Add new state
				newHistory.push({
					filters,
					timestamp: Date.now(),
				});

				// Limit history size
				if (newHistory.length > maxHistorySize) {
					return newHistory.slice(-maxHistorySize);
				}

				return newHistory;
			});

			setCurrentIndex((prev) => {
				const newIndex = prev + 1;
				return newIndex >= maxHistorySize ? maxHistorySize - 1 : newIndex;
			});
		},
		[currentIndex, maxHistorySize],
	);

	// Undo to previous state
	const undo = useCallback((): FilterState | null => {
		if (!canUndo) return null;

		isUndoRedoRef.current = true;
		const newIndex = currentIndex - 1;
		setCurrentIndex(newIndex);
		return history[newIndex].filters;
	}, [canUndo, currentIndex, history]);

	// Redo to next state
	const redo = useCallback((): FilterState | null => {
		if (!canRedo) return null;

		isUndoRedoRef.current = true;
		const newIndex = currentIndex + 1;
		setCurrentIndex(newIndex);
		return history[newIndex].filters;
	}, [canRedo, currentIndex, history]);

	// Clear history and reset to current state
	const clearHistory = useCallback((filters: FilterState) => {
		setHistory([{ filters, timestamp: Date.now() }]);
		setCurrentIndex(0);
	}, []);

	// Get current state
	const getCurrentState = useCallback((): FilterState => {
		return history[currentIndex].filters;
	}, [history, currentIndex]);

	return {
		canUndo,
		canRedo,
		undo,
		redo,
		pushState,
		clearHistory,
		getCurrentState,
		historySize: history.length,
		currentIndex,
	};
}
